import React, { useEffect, useMemo, useRef, useState } from "react";
import PhoneInput, { getCountryCallingCode } from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import "react-phone-number-input/style.css";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/light.css";
import { CalendarDays, ChevronDown, Search } from "lucide-react";

const FlagIcon = ({ country }) => {
  const Flag = country ? flags[country] : null;

  return (
    <span className="flex h-4 w-6 shrink-0 overflow-hidden rounded-sm bg-gray-100 shadow-sm">
      {Flag ? (
        <Flag
          title={country}
          className="h-full w-full object-cover"
        />
      ) : null}
    </span>
  );
};

const CustomCountrySelect = ({ value, onChange, options, disabled, phoneNumberValue }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);

  const countries = useMemo(() => {
    return options.filter((option) => option.value);
  }, [options]);

  let actualCountryValue = value;
  if (!actualCountryValue && phoneNumberValue) {
    const noSpaces = phoneNumberValue.replace(/\s+/g, "");
    let bestMatch = null;
    let maxLen = 0;
    for (const opt of countries) {
      const cc = getCountryCallingCode(opt.value);
      if (noSpaces.startsWith(`+${cc}`)) {
        if (cc.length > maxLen) {
          maxLen = cc.length;
          bestMatch = opt.value;
        }
      }
    }
    if (bestMatch) {
      if (noSpaces.startsWith("+1")) bestMatch = "US";
      actualCountryValue = bestMatch;
    }
  }

  const selectedCountry = countries.find((country) => country.value === actualCountryValue);

  const filteredCountries = countries.filter((country) => {
    const query = search.toLowerCase().trim();
    const countryName = country.label.toLowerCase();
    const countryCode = country.value.toLowerCase();
    const callingCode = getCountryCallingCode(country.value);

    return countryName.includes(query) || countryCode.includes(query) || callingCode.includes(query);
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      className="relative shrink-0">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className="
          flex h-8 items-center gap-2 rounded-md border-0 bg-transparent px-1.5
          transition-all duration-200 hover:bg-[#F8F4FA]
          focus:outline-none focus:ring-0
          disabled:cursor-not-allowed disabled:opacity-60
        ">
        <FlagIcon country={selectedCountry?.value} />

        {/* <span className="text-sm font-semibold text-[#2D0B31]">{selectedCallingCode}</span> */}

        <ChevronDown
          size={15}
          strokeWidth={2.5}
          className={`text-gray-400 transition-transform duration-200 ${open ? "rotate-180 text-[#2D0B31]" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute left-0 top-11 z-[9999] w-80 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-[0_18px_45px_rgba(45,11,49,0.18)]">
          <div className="border-b border-gray-100 bg-white p-3">
            <div className="relative">
              <Search
                size={16}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />

              <input
                type="text"
                value={search}
                autoFocus
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search country or code"
                className="
                  w-full rounded-xl border border-gray-100 bg-[#FAF7FB] py-2.5 pl-9 pr-3 text-sm text-gray-900
                  outline-none transition-all duration-200
                  placeholder:text-gray-400
                "
              />
            </div>
          </div>

          <div className="max-h-72 overflow-y-auto p-2">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => {
                const isSelected = country.value === value;
                const callingCode = `+${getCountryCallingCode(country.value)}`;

                return (
                  <button
                    key={country.value}
                    type="button"
                    onClick={() => {
                      onChange(country.value);
                      setOpen(false);
                      setSearch("");
                    }}
                    className={`
                      group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-all duration-150
                      ${isSelected ? "bg-[#2D0B31] text-white shadow-sm" : "text-gray-700 hover:bg-[#F8F4FA] hover:text-[#2D0B31]"}
                    `}>
                    <FlagIcon country={country.value} />

                    <div className="min-w-0 flex-1">
                      <div className="truncate font-semibold">{country.label}</div>
                    </div>

                    <span className={`rounded-full px-2 py-1 text-xs font-bold ${isSelected ? "bg-white/15 text-white" : "bg-[#F8F4FA] text-[#2D0B31]"}`}>{callingCode}</span>
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-8 text-center text-sm font-semibold text-gray-400">No country found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const TravelFormInput = ({ label, name, type = "text", value, onChange, required = false, placeholder = "", className = "", error, disabled = false }) => {
  const [isFocused, setIsFocused] = useState(false);

  const isFloating = isFocused || (value && value.toString().trim().length > 0);

  const standardInputStyles = `
    w-full rounded-xl border px-4 py-3.5 text-sm text-gray-900 shadow-sm
    transition-all duration-200 
    focus:outline-none focus:ring-1
    ${error ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-gray-200 focus:border-[#2D0B31] focus:ring-[#2D0B31]"}
    ${disabled ? "bg-gray-100 cursor-not-allowed opacity-80" : "bg-white hover:border-gray-300 hover:shadow-md"}
  `;

  const phoneWrapperStyles = `
  w-full flex items-center rounded-xl border px-3 py-2 text-sm text-gray-900 shadow-sm
  transition-all duration-200 
  focus-within:outline-none focus-within:ring-1
  ${error ? "border-red-500 focus-within:border-red-500 focus-within:ring-red-500" : "border-gray-200 focus-within:border-[#2D0B31] focus-within:ring-[#2D0B31]"}
  ${disabled ? "bg-gray-100 cursor-not-allowed opacity-80" : "bg-white hover:border-gray-300 hover:shadow-md"}

  [&_.PhoneInputCountry]:mr-3
  [&_.PhoneInputCountry]:border-r
  [&_.PhoneInputCountry]:border-gray-200
  [&_.PhoneInputCountry]:pr-3

  [&_.PhoneInputInput]:min-w-0
  [&_.PhoneInputInput]:flex-1
  [&_.PhoneInputInput]:w-full
  [&_.PhoneInputInput]:!border-none
  [&_.PhoneInputInput]:!bg-transparent
  [&_.PhoneInputInput]:!p-0
  [&_.PhoneInputInput]:text-sm
  [&_.PhoneInputInput]:font-medium
  [&_.PhoneInputInput]:text-gray-900
  [&_.PhoneInputInput]:placeholder:text-gray-400
  [&_.PhoneInputInput]:!outline-none
  [&_.PhoneInputInput]:!ring-0
`;

  let parsedDate = null;

  if (value) {
    if (value instanceof Date && !isNaN(value.getTime())) {
      parsedDate = value;
    } else if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed.includes("-")) {
        const cleanDate = trimmed.split("T")[0];
        const [year, month, day] = cleanDate.split("-");
        if (year && month && day) {
          parsedDate = new Date(Number(year), Number(month) - 1, Number(day));
        }
      } else if (trimmed.includes(".")) {
        const [day, month, year] = trimmed.split(".");
        if (day && month && year) {
          parsedDate = new Date(Number(year), Number(month) - 1, Number(day));
        }
      } else if (trimmed.includes("/")) {
        const [month, day, year] = trimmed.split("/");
        if (month && day && year) {
          parsedDate = new Date(Number(year), Number(month) - 1, Number(day));
        }
      }
    }
    if (parsedDate && isNaN(parsedDate.getTime())) {
      parsedDate = null;
    }
  }

  const handleDateChange = (selectedDates) => {
    const date = selectedDates[0];

    if (!date) {
      onChange({ target: { name, value: "" } });
      return;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    onChange({ target: { name, value: `${year}-${month}-${day}` } });
  };

  return (
    <div className={`relative flex flex-col rounded-xl bg-transparent ${className}`}>
      {label && (
        <label
          htmlFor={name}
          className={`
            pointer-events-none absolute z-10 bg-white px-1 transition-all duration-200 ease-in-out
            ${isFloating ? `-top-2.5 left-3 text-[11px] font-bold ${error ? "text-red-500" : "text-[#2D0B31]"}` : `${type === "tel" ? "left-[112px]" : "left-3"} top-3.5 text-sm font-medium ${error ? "text-red-500" : "text-gray-500"}`}
          `}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {type === "tel" ? (
        <PhoneInput
          id={name}
          name={name}
          defaultCountry="GB"
          countrySelectComponent={CustomCountrySelect}
          countrySelectProps={{ phoneNumberValue: value }}
          smartCaret={false}
          international={true}
          placeholder={isFloating ? placeholder || "12345 67890" : ""}
          value={value}
          onChange={(phone) => onChange({ target: { name, value: phone || "" } })}
          required={required}
          disabled={disabled}
          className={phoneWrapperStyles}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
      ) : type === "date" ? (
        <div className="relative w-full">
          <Flatpickr
            id={name}
            name={name}
            value={parsedDate}
            onChange={handleDateChange}
            onOpen={() => setIsFocused(true)}
            onClose={() => setIsFocused(false)}
            options={{
              dateFormat: "m/d/Y",
              allowInput: true,
              disableMobile: true,
            }}
            placeholder={isFloating ? placeholder || "MM/DD/YYYY" : ""}
            className={standardInputStyles}
            required={required}
            disabled={disabled}
          />

          <CalendarDays
            size={18}
            className="pointer-events-none absolute right-4 top-[14px] text-gray-400"
          />
        </div>
      ) : (
        <input
          id={name}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          required={required}
          disabled={disabled}
          placeholder={isFloating ? placeholder : ""}
          className={standardInputStyles}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
      )}

      {error && <span className="animate-fade-in mt-1.5 pl-1 text-[11px] font-bold text-red-500">{error}</span>}
    </div>
  );
};

export default TravelFormInput;
